/* ==========================================================================
   FILE: global/hooks/useAPI.js
   IAMONEAI - Legacy API Hook (Compatibility Layer)
   Uses new services underneath for caching, offline, etc.
   ========================================================================== */

import { useState, useCallback } from 'react';
import { authService, chatService, brainService, memoryService } from '../../services';

/**
 * Legacy API hook - wraps new services for backward compatibility
 * @deprecated Use individual hooks (useAuth, useChat, etc.) for new code
 */
export function useAPI() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ==========================================================================
  // CHAT
  // ==========================================================================

  const chat = useCallback(async (text, iin = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await chatService.send(text, iin);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================================================
  // BRAIN
  // ==========================================================================

  const getBrain = useCallback(async (iin) => {
    setLoading(true);
    setError(null);
    try {
      const response = await brainService.get(iin);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================================================
  // MEMORIES
  // ==========================================================================

  const getMemories = useCallback(async (iin, type = null) => {
    setLoading(true);
    setError(null);
    try {
      const options = type ? { type } : {};
      const response = await memoryService.getAll(iin, options);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================================================
  // IDENTITY / AUTH
  // ==========================================================================

  const lookupIdentity = useCallback(async (iin) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.lookup(iin);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyIdentity = useCallback(async (iin, securityAnswer) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.verify(iin, securityAnswer);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.register(userData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================================================
  // VERIFICATION (Email)
  // ==========================================================================

  const sendVerificationCode = useCallback(async (email, recaptchaToken = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.sendCode(email, recaptchaToken);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyCode = useCallback(async (email, code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.verifyCode(email, code);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================================================
  // RETURN
  // ==========================================================================

  return {
    loading,
    error,
    chat,
    getBrain,
    getMemories,
    lookupIdentity,
    getIdentity: lookupIdentity,
    verifyIdentity,
    register,
    sendVerificationCode,
    verifyCode,
  };
}

// ==========================================================================
// SESSION HOOK (unchanged)
// ==========================================================================

export function useSession() {
  const getIIN = () => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('iamoneai_iin');
  };

  const setIIN = (iin) => {
    if (typeof window === 'undefined') return;
    if (iin) {
      localStorage.setItem('iamoneai_iin', iin);
    } else {
      localStorage.removeItem('iamoneai_iin');
    }
  };

  const clearSession = () => {
    localStorage.removeItem('iamoneai_iin');
  };

  return { getIIN, setIIN, clearSession };
}
